import { IconTeam, TeamIcon } from 'components/teamSelect/IconTeam';
import ButtonIcon from 'components/buttonIcon';
import Tooltip from 'components/tooltip';
import { DeadlinePolicy } from 'contexts/playlists/types';
import {
  MANAGE_TEAM_DEADLINE_TOOLTIP,
  REMOVE_TEAM_ASSIGNMENT_TOOLTIP,
  TEAM_DEADLINES_PANEL_TITLE,
} from 'constants/playlists';
import { pluralize } from 'utils/string';
import { computeDeadlineWeeksToComplete } from 'pages/Playlists/components/ManageDeadlineDrawer/deadlineWeeks';
import {
  TeamAssignmentDeadlineContainer,
  TeamAssignmentDeadlineTitle,
  TeamAssignmentInfo,
  TeamAssignmentName,
  TeamAssignmentPill,
  TeamAssignmentPillHighlight,
  TeamAssignmentPills,
  TeamAssignmentRow,
  TeamAssignmentRowActions,
} from './teamAssignmentDeadlineStyles';

const TOOLTIP_Z_INDEX = 9999;

export interface TeamDeadlineRow {
  teamId: string;
  name: string;
  iconName?: TeamIcon;
  iconColor?: string;
  activeUsersCount: number | null;
  deadlinePolicy: (DeadlinePolicy & { activated_at?: string }) | null;
  canManage: boolean;
}

export interface TeamAssignmentDeadlineMenuProps {
  teams: TeamDeadlineRow[];
  playlistDurationSeconds?: number;
  loadingTeamId?: string | null;
  onManageDeadline?: (teamId: string) => void;
  onRemoveAssignment?: (teamId: string) => void;
}

const renderDeadlineLabel = (
  deadlinePolicy: (DeadlinePolicy & { activated_at?: string }) | null,
  playlistDurationSeconds?: number
): JSX.Element => {
  if (!deadlinePolicy) return <>Deadline: none</>;

  if (!playlistDurationSeconds || playlistDurationSeconds <= 0) {
    return <>Deadline: calculating duration...</>;
  }

  const weeksToComplete = computeDeadlineWeeksToComplete(
    playlistDurationSeconds,
    deadlinePolicy.training_pace
  );

  return (
    <>
      Deadline:{' '}
      <TeamAssignmentPillHighlight>
        {weeksToComplete} week{weeksToComplete === 1 ? '' : 's'} to complete
      </TeamAssignmentPillHighlight>
    </>
  );
};

const TeamAssignmentDeadlineMenu = ({
  teams,
  playlistDurationSeconds,
  loadingTeamId,
  onManageDeadline,
  onRemoveAssignment,
}: TeamAssignmentDeadlineMenuProps): JSX.Element => (
  <TeamAssignmentDeadlineContainer>
    <TeamAssignmentDeadlineTitle>{TEAM_DEADLINES_PANEL_TITLE}</TeamAssignmentDeadlineTitle>
    {teams.map((team) => (
      <TeamAssignmentRow key={team.teamId}>
        <IconTeam iconName={team.iconName} color={team.iconColor} size="smaller" />
        <TeamAssignmentInfo>
          <TeamAssignmentName>{team.name}</TeamAssignmentName>
          <TeamAssignmentPills>
            <TeamAssignmentPill>{pluralize(team.activeUsersCount ?? 0, 'user')}</TeamAssignmentPill>
            {team.canManage && (
              <TeamAssignmentPill>
                {renderDeadlineLabel(team.deadlinePolicy, playlistDurationSeconds)}
              </TeamAssignmentPill>
            )}
          </TeamAssignmentPills>
        </TeamAssignmentInfo>
        <TeamAssignmentRowActions>
          {team.canManage && (
            <Tooltip placement="top" title={MANAGE_TEAM_DEADLINE_TOOLTIP} zIndex={TOOLTIP_Z_INDEX}>
              <ButtonIcon
                $terciary
                size="small"
                icon="CalendarTransparent"
                aria-label={MANAGE_TEAM_DEADLINE_TOOLTIP}
                loading={loadingTeamId === team.teamId}
                disabled={loadingTeamId === team.teamId}
                onClick={() => onManageDeadline?.(team.teamId)}
              />
            </Tooltip>
          )}
          <Tooltip placement="top" title={REMOVE_TEAM_ASSIGNMENT_TOOLTIP} zIndex={TOOLTIP_Z_INDEX}>
            <ButtonIcon
              $terciary
              size="small"
              icon="Trash"
              aria-label={REMOVE_TEAM_ASSIGNMENT_TOOLTIP}
              onClick={() => onRemoveAssignment?.(team.teamId)}
            />
          </Tooltip>
        </TeamAssignmentRowActions>
      </TeamAssignmentRow>
    ))}
  </TeamAssignmentDeadlineContainer>
);

export default TeamAssignmentDeadlineMenu;
